import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { FieldFilter } from 'module/types/filter-field/filter';
import { Button } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { criterion } from 'module/ui/components/molecules/field-filter/criteria/criterion';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Form as AntForm } from 'antd';
import { startCase } from 'lodash';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Text, Fields, StyledFieldsOption } from './components';
import { getDefaultCriteria } from './get-default-criteria';
import { BaseFilterComponentProps } from '../types';

const StyledText = styled(Text)`
  min-width: var(--spacing-08);
`;

const NoMatches = styled.div`
  text-align: center;
`;

const OptionContent = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  min-width: 0;
`;

const OptionMainText = styled.span`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Match pill styling used in `components/organisms/integrations/salesforce/create/action-drawer.tsx`
const IntegrationFieldIdPill = styled.span`
  flex-shrink: 0;
  max-width: calc(var(--spacing-03) * 25);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: var(--spacing-01) var(--spacing-03);
  border-radius: var(--border-radius-xsmall);
  background: var(--ozone-magenta-surface);
  color: var(--ozone-magenta);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-01);
  font-weight: var(--font-weight-semi-bold);
`;

const Form = styled(AntForm)`
  .ant-row.ant-form-item:last-child {
    margin-right: 0;
  }
`;

interface Props extends BaseFilterComponentProps {
  index?: number;
  hideMatchTypeText?: boolean;
  hideRemoveButton?: boolean;
  matchTypeLabel: string | React.ReactNode;
  fields: FilterableField[];
  filter?: FieldFilter;
  onChange: (filter: FieldFilter) => void;
  onRemove?: () => void;
}

export const FieldsSelect = ({
  fields,
  filter,
  onChange,
}: Pick<Props, 'fields' | 'filter' | 'onChange'>) => {
  return (
    <Fields
      width={240}
      virtual={false}
      data-cy="select.fields"
      data-cy-field={`select.fields.${filter?.field?.fieldLabel ?? '__new__'}`}
      placeholder="Select..."
      value={filter?.field?.fieldId}
      optionLabelProp="children"
      onChange={(selected) =>
        onChange(
          getDefaultCriteria(
            fields.find((f) => String(f.fieldId) === String(selected))!,
          ),
        )
      }
      showSearch
      optionFilterProp="label"
      notFoundContent={
        <NoMatches>
          <FormattedMessage id="task_list.filters.no_fields_match" />
        </NoMatches>
      }
    >
      {fields.map((f) =>
        (() => {
          const label = f.fieldLabel || startCase(f.fieldName);
          const pillText = (f.metaData as any)?.pillText as string | undefined;
          const showPill = Boolean(pillText);

          return (
            <StyledFieldsOption
              key={String(f.fieldId)}
              value={f.fieldId}
              title={f.description}
              label={label}
            >
              <span
                data-cy={`select.fields.dropdown.${f.fieldName}`}
                title={f.fieldName}
              >
                {showPill ? (
                  <OptionContent>
                    <OptionMainText title={label}>{label}</OptionMainText>
                    <IntegrationFieldIdPill title={pillText}>
                      {pillText}
                    </IntegrationFieldIdPill>
                  </OptionContent>
                ) : (
                  label
                )}
              </span>
            </StyledFieldsOption>
          );
        })(),
      )}
    </Fields>
  );
};

// TODO: Refactor out into separate criterion components
export const Criteria: FC<Props> = ({
  index = 0,
  hideMatchTypeText = false,
  hideRemoveButton = false,
  fields,
  matchTypeLabel: text,
  filter,
  onChange,
  onRemove,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  getOperationOptionRenderer,
  userOptionsGetter,
  className,
}) => (
  <Form layout="inline" className={className}>
    {hideRemoveButton ? null : (
      <Form.Item>
        <Button
          type="text"
          shape="circle"
          size="small"
          icon={<FontAwesomeIcon icon={faXmark} />}
          style={{ visibility: onRemove != null ? 'visible' : 'hidden' }}
          onClick={onRemove}
          data-cy={`advanced.filter.remove.button.${index}`}
        />
      </Form.Item>
    )}
    {hideMatchTypeText ? null : (
      <Form.Item>
        <StyledText>{text}</StyledText>
      </Form.Item>
    )}
    <Form.Item data-cy="advanced_filter_trigger">
      <FieldsSelect fields={fields} filter={filter} onChange={onChange} />
    </Form.Item>
    {criterion({
      filter,
      onChange,
      getFilterOperationTypeOptions,
      getSelectEditorComponents,
      getOperationOptionRenderer,
      userOptionsGetter,
    })}
  </Form>
);

export * from './grouped-criteria';
